import type { BaseClient } from '../../http/BaseClient.js';
import { unwrapData } from '../../common/envelope.js';
import type { ProductKind } from '../projects/types.js';
import type {
  AcceptInviteRequest,
  AddUserToOrgRequest,
  AddUserToOrgResult,
  AdjustOrgCreditsRequest,
  AdjustOrgCreditsResult,
  AdminOrgSummaryQuery,
  AdminOrgSummaryResponse,
  CreateOrganizationRequest,
  CreateOrganizationUserRequest,
  InviteOrgRequest,
  InviteOrgResult,
  OrgCreditBalance,
  OrgInvite,
  OrgNotifySubscriptions,
  OrgSearchResult,
  Organization,
  OrganizationUser,
  SetOrgNotifySubscriptionsRequest,
  SimpleOrgResponse,
  UpdateOrganizationRequest,
  UpdateOrgUserRequest,
} from './types.js';

export class OrganizationClient {
  constructor(private readonly http: BaseClient) {}

  async get(orgId: string): Promise<Organization> {
    const res = await this.http.get<{ success: boolean; data: Organization }>(`/organization/${orgId}`);
    return unwrapData(res);
  }

  async update(orgId: string, body: UpdateOrganizationRequest): Promise<Organization> {
    const res = await this.http.put<{ success: boolean; data: Organization }>(`/organization/${orgId}`, { body });
    return unwrapData(res);
  }

  /**
   * Self-service: create an org for the current (orgless) user and assign them
   * as Owner. Used by the onboarding wizard so the org exists before the limits
   * and project/sandbox steps. Fails if the user already belongs to an org.
   */
  async create(body: CreateOrganizationRequest): Promise<Organization> {
    const res = await this.http.post<{ success: boolean; data: Organization }>('/organization', { body });
    return unwrapData(res);
  }

  /**
   * Fetch the org's default-notification-channel config: the per-event
   * subscriptions, the notify channels available to route to, and whether the
   * caller's org is the platform-admin org (which unlocks the cross-org
   * `engine_failure` / `infra_outage` event types).
   */
  async getNotifySubscriptions(): Promise<OrgNotifySubscriptions> {
    const res = await this.http.get<{ success: boolean; data: OrgNotifySubscriptions }>(
      '/organization/notify-subscriptions',
    );
    return unwrapData(res);
  }

  /**
   * Replace the org's notify subscriptions (replace-set). Each edge is
   * validated server-side: the credential must be a notify channel in the org,
   * and admin-only event types require a platform-admin caller.
   */
  setNotifySubscriptions(body: SetOrgNotifySubscriptionsRequest): Promise<SimpleOrgResponse> {
    return this.http.put<SimpleOrgResponse>('/organization/notify-subscriptions', { body });
  }

  async listUsers(): Promise<OrganizationUser[]> {
    const res = await this.http.get<{ success: boolean; data: OrganizationUser[] }>('/organization/users');
    return res.data ?? [];
  }

  updateUser(userId: string, body: UpdateOrgUserRequest): Promise<SimpleOrgResponse> {
    return this.http.put<SimpleOrgResponse>(`/organization/users/${userId}`, { body });
  }

  /** Backend currently returns 501. Surfaced for forward-compat. */
  createUser(body: CreateOrganizationUserRequest): Promise<SimpleOrgResponse> {
    return this.http.post<SimpleOrgResponse>('/organization/users', { body });
  }

  /** Backend currently returns 501. Surfaced for forward-compat. */
  deleteUser(userId: string): Promise<SimpleOrgResponse> {
    return this.http.delete<SimpleOrgResponse>(`/organization/users/${userId}`);
  }

  /** Accept an org invite: verifies the one-time token and sets the user's password. */
  acceptInvite(body: AcceptInviteRequest): Promise<SimpleOrgResponse> {
    return this.http.post<SimpleOrgResponse>('/organization/invite/accept', { body });
  }

  /**
   * Platform-admin only: list every org for the impersonation picker.
   * The server returns 403 when the caller's home org is not the
   * configured ADMIN_ORGANIZATION; render the picker only when the
   * authenticated user has `isPlatformAdmin === true`.
   */
  async listAll(): Promise<Array<{ id: string; name: string; allowDebug: boolean }>> {
    const res = await this.http.get<{
      success: boolean;
      data: Array<{ id: string; name: string; allowDebug: boolean }>;
    }>('/orgs/list-all');
    return res.data ?? [];
  }

  /**
   * Platform-admin only: typeahead org search. Returns up to `limit` matches
   * (id+name) for the case-insensitive name substring `q` (or the first
   * `limit` orgs when `q` is empty) plus the total match count, so a picker
   * can show "N more — refine your search". Returns 403 unless platform admin.
   */
  async searchOrgs(q = '', limit = 10): Promise<OrgSearchResult> {
    const res = await this.http.get<{ success: boolean; data: OrgSearchResult }>('/orgs/search', {
      query: { q, limit },
    });
    return unwrapData(res);
  }

  /**
   * Platform-admin only: one page of the service-wide org summary — per-org
   * user/project/sandbox counts, trailing 7d/30d metered usage, billed-to-
   * balance, lifetime purchases and current credit balance — searchable by
   * name and pageable. Backs the admin Summary screen. Returns 403 unless the
   * caller's home org is the configured ADMIN_ORGANIZATION.
   */
  async adminOrgSummary(query: AdminOrgSummaryQuery = {}): Promise<AdminOrgSummaryResponse> {
    const res = await this.http.get<{ success: boolean; data: AdminOrgSummaryResponse }>(
      '/admin/orgs/summary',
      { query: query as Record<string, unknown> },
    );
    return unwrapData(res);
  }

  /**
   * Platform-admin only: provision a new organization + founding owner and
   * email the owner an invite link. The invite-only counterpart to public
   * self-service registration (used while the backend has OPEN_REGISTRATION
   * disabled). Returns 403 unless the caller's home org is the configured
   * ADMIN_ORGANIZATION; 409 when the owner email already exists.
   */
  async inviteOrg(body: InviteOrgRequest): Promise<InviteOrgResult> {
    const res = await this.http.post<{ success: boolean; data: InviteOrgResult }>(
      '/organization/invite-org',
      { body },
    );
    return unwrapData(res);
  }

  /**
   * Platform-admin only: list every outstanding org + member invite (founding-
   * owner invites from `inviteOrg` and members added via `addUserToOrg`) with
   * derived status, newest first. Cancelled invites (soft-deleted) and live
   * non-invited orgs are excluded. Returns 403 unless platform admin.
   */
  async listOrgInvites(): Promise<OrgInvite[]> {
    const res = await this.http.get<{ success: boolean; data: OrgInvite[] }>('/admin/org-invites');
    return res.data ?? [];
  }

  /**
   * Platform-admin only: re-send an invite — mints a fresh 7-day token and
   * re-emails the link. Returns 403 unless platform admin; 404 when no pending
   * invite exists; 409 once the invite has been redeemed.
   */
  resendOrgInvite(userId: string): Promise<SimpleOrgResponse> {
    return this.http.post<SimpleOrgResponse>(`/admin/org-invites/${userId}/resend`, {});
  }

  /**
   * Platform-admin only: cancel a pending invite. Soft-deletes the invited user
   * (and the shell org, for a never-redeemed founding-owner invite with no other
   * members) and revokes the token, freeing the email for re-invite. Returns 403
   * unless platform admin; 404 when absent; 409 once the invite has been redeemed.
   */
  cancelOrgInvite(userId: string): Promise<SimpleOrgResponse> {
    return this.http.post<SimpleOrgResponse>(`/admin/org-invites/${userId}/cancel`, {});
  }

  /**
   * Platform-admin only: enable or disable the debug surface for an org.
   * Returns the updated org entry with the new allowDebug value.
   */
  async setAllowDebug(orgId: string, allow: boolean): Promise<{ id: string; name: string; allowDebug: boolean }> {
    const res = await this.http.patch<{
      success: boolean;
      data: { id: string; name: string; allowDebug: boolean };
    }>(`/orgs/${orgId}/allow-debug`, { body: { allow } });
    return res.data!;
  }

  /**
   * Platform-admin only: replace an org's product entitlement. `products` is
   * the full desired set ('cloud' | 'bi'); unknown codes are rejected 400.
   * Returns the updated org's id, name and enabledProducts.
   */
  async setEnabledProducts(
    orgId: string,
    products: ProductKind[],
  ): Promise<{ id: string; name: string; enabledProducts: ProductKind[] }> {
    const res = await this.http.patch<{
      success: boolean;
      data: { id: string; name: string; enabledProducts: ProductKind[] };
    }>(`/orgs/${orgId}/enabled-products`, { body: { products } });
    return res.data!;
  }

  /**
   * Platform-admin only: add a new member to an existing org.
   * Creates the user without a password and sends an invite email via the
   * same TokenOrgInvite flow as the regular member invite.
   * Returns 403 unless platform admin; 409 when the email already exists.
   */
  async addUserToOrg(orgId: string, body: AddUserToOrgRequest): Promise<AddUserToOrgResult> {
    const res = await this.http.post<{ success: boolean; data: AddUserToOrgResult }>(
      `/orgs/${orgId}/users`,
      { body },
    );
    return unwrapData(res);
  }

  /**
   * Platform-admin only: fetch an org's two-bucket credit balance.
   * Returns 403 unless platform admin; 404 when the org doesn't exist.
   */
  async getOrgCreditBalance(orgId: string): Promise<OrgCreditBalance> {
    const res = await this.http.get<{ success: boolean; data: OrgCreditBalance }>(
      `/orgs/${orgId}/credits`,
    );
    return unwrapData(res);
  }

  /**
   * Platform-admin only: grant or revoke credits on an org.
   * amountMicros is signed: positive = grant, negative = revoke.
   * Records a CreditKindAdminAdjust ledger entry and returns the updated balance.
   */
  async adjustOrgCredits(orgId: string, body: AdjustOrgCreditsRequest): Promise<AdjustOrgCreditsResult> {
    const res = await this.http.post<{ success: boolean; data: AdjustOrgCreditsResult }>(
      `/orgs/${orgId}/credits/adjust`,
      { body },
    );
    return unwrapData(res);
  }
}
